import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme} from '@mui/material/styles';
import {usePageContext} from '../context/PageContext';

type BreakpointKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
const BREAKPOINT_ORDER: BreakpointKey[] = ['xs', 'sm', 'md', 'lg', 'xl'];

/*
* Assumed viewport for the very first render (server-side render and the initial client
* render before hydration). Without this, useMediaQuery has no browser to query yet and every
* query would resolve to `false` on first paint, which used to be masked by an `isLoadingBreakpoints`
* flag that skipped rendering entirely until the client figured out the real viewport - hiding page
* content (including the H1) from search engines and LLM crawlers that don't execute JavaScript.
* matchMedia listeners correct these values right after mount if the real viewport differs.
*
* The guess itself (mobile vs. desktop) comes from the request's User-Agent header (resolved in
* getServerSideProps, see pages/[[...slug]].tsx, and threaded down via PageContext), so it matches
* the real device for most visitors instead of a single fixed default - a fixed desktop-only
* default previously caused a large layout shift for mobile visitors once the client corrected it.
* */
const useWindowSize = () => {
    const theme = useTheme();
    const {isMobileUserAgent} = usePageContext();
    const defaultBreakpoint: BreakpointKey = isMobileUserAgent ? 'xs' : 'lg';
    const defaultBreakpointIndex = BREAKPOINT_ORDER.indexOf(defaultBreakpoint);

    const isDefaultBreakpoint = (key: BreakpointKey) => key === defaultBreakpoint;
    const isAtLeastDefaultBreakpoint = (key: BreakpointKey) => BREAKPOINT_ORDER.indexOf(key) <= defaultBreakpointIndex;
    const isBelowDefaultBreakpoint = (key: BreakpointKey) => defaultBreakpointIndex < BREAKPOINT_ORDER.indexOf(key);

    /*
    * A media query string ready to be used with most styling solutions, which matches screen widths
    * starting from the screen size given by the breakpoint key (inclusive) and
    * stopping at the screen size given by the next breakpoint key (exclusive).
    * */
    const isWindowExtraSmall = useMediaQuery(theme.breakpoints.only('xs'), {defaultMatches: isDefaultBreakpoint('xs')});
    const isWindowSmall = useMediaQuery(theme.breakpoints.only('sm'), {defaultMatches: isDefaultBreakpoint('sm')});
    const isWindowMedium = useMediaQuery(theme.breakpoints.only('md'), {defaultMatches: isDefaultBreakpoint('md')});
    const isWindowLarge = useMediaQuery(theme.breakpoints.only('lg'), {defaultMatches: isDefaultBreakpoint('lg')});
    const isWindowExtraLarge = useMediaQuery(theme.breakpoints.only('xl'), {defaultMatches: isDefaultBreakpoint('xl')});

    /*
    * A media query string ready to be used with most styling solutions, which matches screen widths
    * greater than the screen size given by the breakpoint key (inclusive).
    * */
    const greaterThanWindowExtraSmall = useMediaQuery(theme.breakpoints.up('xs'), {defaultMatches: isAtLeastDefaultBreakpoint('xs')});
    const greaterThanWindowSmall = useMediaQuery(theme.breakpoints.up('sm'), {defaultMatches: isAtLeastDefaultBreakpoint('sm')});
    const greaterThanWindowMedium = useMediaQuery(theme.breakpoints.up('md'), {defaultMatches: isAtLeastDefaultBreakpoint('md')});
    const greaterThanWindowLarge = useMediaQuery(theme.breakpoints.up('lg'), {defaultMatches: isAtLeastDefaultBreakpoint('lg')});
    const greaterThanWindowExtraLarge = useMediaQuery(theme.breakpoints.up('xl'), {defaultMatches: isAtLeastDefaultBreakpoint('xl')});

    /*
    * A media query string ready to be used with most styling solutions, which matches screen widths
    * less than the screen size given by the breakpoint key (exclusive).
    * */
    const lowerThanWindowExtraSmall = useMediaQuery(theme.breakpoints.down('xs'), {defaultMatches: isBelowDefaultBreakpoint('xs')});
    const lowerThanWindowSmall = useMediaQuery(theme.breakpoints.down('sm'), {defaultMatches: isBelowDefaultBreakpoint('sm')});
    const lowerThanWindowMedium = useMediaQuery(theme.breakpoints.down('md'), {defaultMatches: isBelowDefaultBreakpoint('md')});
    const lowerThanWindowLarge = useMediaQuery(theme.breakpoints.down('lg'), {defaultMatches: isBelowDefaultBreakpoint('lg')});
    const lowerThanWindowExtraLarge = useMediaQuery(theme.breakpoints.down('xl'), {defaultMatches: isBelowDefaultBreakpoint('xl')});

    return {
        isWindowExtraSmall,
        isWindowSmall,
        isWindowMedium,
        isWindowLarge,
        isWindowExtraLarge,
        greaterThanWindowExtraSmall,
        greaterThanWindowSmall,
        greaterThanWindowMedium,
        greaterThanWindowLarge,
        greaterThanWindowExtraLarge,
        lowerThanWindowExtraSmall,
        lowerThanWindowSmall,
        lowerThanWindowMedium,
        lowerThanWindowLarge,
        lowerThanWindowExtraLarge
    };
};

export default useWindowSize;
